/**
 * EditModal.jsx — Task Edit Modal
 * Restyled to match the indigo enterprise design system from AttributeModals.jsx.
 * All logic, state, and handlers are unchanged.
 */

import { useState, useEffect } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// FONT INJECTION
// ─────────────────────────────────────────────────────────────────────────────
let _fontsInjected = false;
function injectFonts() {
  if (_fontsInjected || typeof document === "undefined") return;
  _fontsInjected = true;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);

  const style = document.createElement("style");
  style.textContent = `
    *, *::before, *::after { box-sizing: border-box; -webkit-font-smoothing: antialiased; }
    :root { color-scheme: light; }
    @keyframes _backdropIn  { from { opacity:0 } to { opacity:1 } }
    @keyframes _modalIn     { from { opacity:0; transform:translateY(16px) scale(.97) } to { opacity:1; transform:translateY(0) scale(1) } }
    @keyframes _fadeUp      { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
    ._em-scroll::-webkit-scrollbar { width: 4px }
    ._em-scroll::-webkit-scrollbar-track { background: transparent }
    ._em-scroll::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px }
    ._em-scroll::-webkit-scrollbar-thumb:hover { background: #9ca3af }
    ._em-input:focus { border-color: #4338ca !important; box-shadow: 0 0 0 2px #4338ca33 !important; }
    ._em-input:hover:not(:focus) { border-color: #6b7280 !important; }
    ._em-select:focus { border-color: #4338ca !important; box-shadow: 0 0 0 2px #4338ca33 !important; outline: none; }
    ._em-btn-ghost:hover { border-color: #4338ca !important; color: #4338ca !important; }
    ._em-btn-primary:hover { background-color: #3730a3 !important; }
    ._em-checkbox:focus-within { outline: 2px solid #4338ca33; border-radius: 3px; }
  `;
  document.head.appendChild(style);
}

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — Indigo Enterprise (mirrors AttributeModals.jsx)
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  body:        "#f3f4f6",
  card:        "#ffffff",
  blueTint:    "#eef2ff",
  border:      "#d1d5db",
  borderMid:   "#9ca3af",
  borderFocus: "#4338ca",
  ink:         "#111827",
  sub:         "#1f2937",
  muted:       "#4b5563",
  faint:       "#9ca3af",
  indigo50:    "#eef2ff",
  indigo100:   "#e0e7ff",
  indigo200:   "#c7d2fe",
  indigo600:   "#4338ca",
  indigo700:   "#3730a3",
  indigo800:   "#312e81",
  gradStart:   "#312e81",
  gradMid:     "#4338ca",
  gradEnd:     "#6d28d9",
  red50:       "#fee2e2",
  red200:      "#fca5a5",
  red600:      "#b91c1c",
  green500:    "#22c55e",
  green600:    "#16a34a",
  r6: "6px", r8: "8px", r10: "10px", r12: "12px", r16: "16px",
};

const ff     = "'Inter', system-ui, sans-serif";
const ffMono = "'JetBrains Mono', Consolas, monospace";

// ─────────────────────────────────────────────────────────────────────────────
// SHARED ATOMS
// ─────────────────────────────────────────────────────────────────────────────
function SectionLabel({ children, required, hint }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: ".06em", textTransform: "uppercase", fontFamily: ff }}>
        {children}
        {required && <span style={{ color: C.red600, marginLeft: 3 }}>*</span>}
      </span>
      {hint && <div style={{ fontSize: 11, color: C.faint, marginTop: 2, fontFamily: ff }}>{hint}</div>}
    </div>
  );
}

function CheckIcon({ size = 10, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <path d="M3 7l3 3 5-6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL HEADER — matches AttributeModals gradient header with close button
// ─────────────────────────────────────────────────────────────────────────────
function ModalHeader({ onClose }) {
  return (
    <div style={{ background: `linear-gradient(135deg, ${C.gradStart} 0%, ${C.gradMid} 55%, ${C.gradEnd} 100%)`, flexShrink: 0, position: "relative", overflow: "hidden" }}>
      {/* Decorative circles */}
      <div style={{ position: "absolute", right: -40, top: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,.06)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", right: 30, bottom: -60, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,.04)", pointerEvents: "none" }} />

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "22px 24px 22px", position: "relative" }}>
        <div>
          <div style={{ marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 99, background: "rgba(255,255,255,.18)", color: "rgba(255,255,255,.9)", fontFamily: ff }}>
              Editing
            </span>
          </div>
          <div style={{ fontSize: 19, fontWeight: 800, color: "#fff", letterSpacing: "-.03em", fontFamily: ff, lineHeight: 1.2 }}>Edit Task</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.7)", marginTop: 3, fontFamily: ff }}>Update the details and configuration for this task</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{ width: 30, height: 30, borderRadius: C.r6, border: "1px solid rgba(255,255,255,.25)", background: "rgba(255,255,255,.12)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, marginLeft: 12, outline: "none", transition: "all .15s" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.22)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.12)"; }}
        >
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M1 1l9 9M10 1L1 10" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLED INPUT — with focus/hover states matching AttributeModals
// ─────────────────────────────────────────────────────────────────────────────
function StyledInput({ value, onChange, placeholder, style: extraStyle }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="_em-input"
      style={{
        width: "100%",
        padding: "9px 12px",
        border: `1.5px solid ${focused ? C.borderFocus : C.borderMid}`,
        borderRadius: C.r8,
        fontSize: 14,
        fontFamily: ff,
        color: C.ink,
        outline: "none",
        background: C.card,
        transition: "border-color .15s, box-shadow .15s",
        boxShadow: focused ? "0 0 0 2px #4338ca33" : "none",
        ...extraStyle,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLED SELECT
// ─────────────────────────────────────────────────────────────────────────────
function StyledSelect({ value, onChange, children, style: extraStyle }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      className="_em-select"
      value={value}
      onChange={onChange}
      style={{
        width: "100%",
        padding: "9px 36px 9px 12px",
        border: `1.5px solid ${focused ? C.borderFocus : C.borderMid}`,
        borderRadius: C.r8,
        fontSize: 13,
        fontFamily: ff,
        color: C.ink,
        outline: "none",
        background: C.card,
        WebkitAppearance: "none",
        MozAppearance: "none",
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='none' stroke='%234b5563' stroke-width='2' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><path stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/></svg>")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 12px center",
        backgroundSize: "14px",
        transition: "border-color .15s, box-shadow .15s",
        boxShadow: focused ? "0 0 0 2px #4338ca33" : "none",
        cursor: "pointer",
        ...extraStyle,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STATUS BADGE — small colored pill shown inline
// ─────────────────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    "completed":    { bg: "#dcfce7", color: "#166534", border: "#86efac" },
    "in-progress":  { bg: C.indigo100, color: C.indigo800, border: C.indigo200 },
    "pending":      { bg: "#fef9c3", color: "#854d0e", border: "#fde047" },
    "not-started":  { bg: C.body, color: C.muted, border: C.border },
    "blocked":      { bg: C.red50, color: C.red600, border: C.red200 },
  };
  const t = map[status] || map["not-started"];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700, fontFamily: ff, letterSpacing: ".04em", background: t.bg, color: t.color, border: `1px solid ${t.border}` }}>
      {status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ")}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DEPENDENCY CHECKBOX LIST
// ─────────────────────────────────────────────────────────────────────────────
function DependencyList({ availableSteps, selectedIds, onChange }) {
  const toggle = (id, checked) => {
    if (checked) onChange([...selectedIds, id]);
    else onChange(selectedIds.filter(x => x !== id));
  };

  return (
    <div style={{ maxHeight: 140, overflowY: "auto", border: `1.5px solid ${C.borderMid}`, borderRadius: C.r8, padding: "4px 2px" }} className="_em-scroll">
      {availableSteps.length === 0 && (
        <div style={{ padding: "12px 12px", fontSize: 12, color: C.faint, fontFamily: ff }}>No other tasks available</div>
      )}
      {availableSteps.map((step, i) => {
        const checked = selectedIds.includes(step.id);
        return (
          <label
            key={step.id}
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", cursor: "pointer", borderRadius: 6,
              background: checked ? C.blueTint : "transparent",
              transition: "background .1s",
              borderBottom: i < availableSteps.length - 1 ? `1px solid ${C.border}` : "none",
            }}
            onMouseEnter={e => { if (!checked) e.currentTarget.style.background = C.body; }}
            onMouseLeave={e => { if (!checked) e.currentTarget.style.background = "transparent"; }}
          >
            {/* Custom checkbox */}
            <div style={{ position: "relative", width: 16, height: 16, flexShrink: 0 }}>
              <input
                type="checkbox"
                checked={checked}
                onChange={e => toggle(step.id, e.target.checked)}
                style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", margin: 0, cursor: "pointer", zIndex: 1 }}
              />
              <div style={{
                width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${checked ? C.indigo600 : C.borderMid}`,
                background: checked ? C.indigo600 : C.card,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all .12s",
                pointerEvents: "none",
              }}>
                {checked && <CheckIcon size={9} color="#fff" />}
              </div>
            </div>
            <span style={{ fontSize: 13, color: checked ? C.indigo700 : C.sub, fontFamily: ff, fontWeight: checked ? 600 : 400, flex: 1 }}>{step.title}</span>
          </label>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FIELD INPUT RENDERER — matches renderFieldEditInput logic from original
// ─────────────────────────────────────────────────────────────────────────────
function FieldEditInput({ field, value, onChange }) {
  const [focused, setFocused] = useState(false);
  const baseInput = {
    width: "100%", padding: "9px 12px", border: `1.5px solid ${focused ? C.borderFocus : C.borderMid}`,
    borderRadius: C.r8, fontSize: 13, fontFamily: ff, color: C.ink, outline: "none",
    background: C.card, transition: "border-color .15s, box-shadow .15s",
    boxShadow: focused ? "0 0 0 2px #4338ca33" : "none",
  };

  if (field.type === "boolean") {
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "8px 0" }}>
        <div style={{ position: "relative", width: 16, height: 16 }}>
          <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
            style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", margin: 0, cursor: "pointer", zIndex: 1 }} />
          <div style={{
            width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${value ? C.indigo600 : C.borderMid}`,
            background: value ? C.indigo600 : C.card, display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all .12s", pointerEvents: "none",
          }}>
            {value && <CheckIcon size={9} color="#fff" />}
          </div>
        </div>
        <span style={{ fontSize: 13, color: C.sub, fontFamily: ff }}>{value ? "True" : "False"}</span>
      </label>
    );
  }

  if (field.type === "date" || field.type === "date_time") {
    return (
      <input
        type={field.type === "date_time" ? "datetime-local" : "date"}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        style={baseInput}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    );
  }

  if (field.type === "number") {
    return (
      <input
        type="number"
        value={value ?? ""}
        onChange={e => onChange(e.target.value)}
        style={baseInput}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    );
  }

  if (field.type === "select" && field.options?.length) {
    return (
      <StyledSelect value={value || ""} onChange={e => onChange(e.target.value)}>
        <option value="">Select…</option>
        {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </StyledSelect>
    );
  }

  if (field.type === "textarea" || field.type === "long_text") {
    return (
      <textarea
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        rows={3}
        style={{ ...baseInput, resize: "vertical", lineHeight: 1.6 }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    );
  }

  return (
    <input
      type="text"
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      style={baseInput}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PORTAL TOGGLE — matches sharedInPortal checkbox styling
// ─────────────────────────────────────────────────────────────────────────────
function PortalToggle({ checked, onChange }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "10px 14px", borderRadius: C.r8, border: `1.5px solid ${checked ? C.indigo200 : C.border}`, background: checked ? C.blueTint : C.card, transition: "all .15s", userSelect: "none" }}>
      <div style={{ position: "relative", width: 16, height: 16 }}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
          style={{ position: "absolute", opacity: 0, width: "100%", height: "100%", margin: 0, cursor: "pointer", zIndex: 1 }} />
        <div style={{
          width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${checked ? C.indigo600 : C.borderMid}`,
          background: checked ? C.indigo600 : C.card, display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .12s", pointerEvents: "none",
        }}>
          {checked && <CheckIcon size={9} color="#fff" />}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: checked ? C.indigo700 : C.sub, fontFamily: ff }}>Shared in Portal</div>
        <div style={{ fontSize: 11, color: C.faint, fontFamily: ff, marginTop: 1 }}>Visible to clients in the client portal</div>
      </div>
      {checked && (
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 16, height: 16, borderRadius: "50%", background: C.indigo600, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CheckIcon size={8} color="#fff" />
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, color: C.indigo700, fontFamily: ff }}>Active</span>
        </div>
      )}
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EDIT MODAL — main export
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Props:
 *   editingStep       — { groupId, step: { id, title, documentation, status, sharedInPortal, dependencies, additional_fields } }
 *   setEditingStep    — setter
 *   updateStep        — fn(groupId, stepId, stepData)
 *   availableSteps    — [{ id, title }]
 *   taskGroups        — [{ id, additional_fields: [{ name, type }] }]
 *   statusOptions     — string[]
 *   RichTextEditor    — component ({ value, onChange, placeholder })
 */
export default function EditModal({
  editingStep,
  setEditingStep,
  updateStep,
  availableSteps = [],
  taskGroups = [],
  statusOptions = [],
  RichTextEditor,
}) {
  injectFonts();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!editingStep) return null;

  const { step } = editingStep;
  const currentGroup = taskGroups.find(g => g.id === editingStep.groupId);
  const additionalFields = currentGroup?.additional_fields || [];

  const handleClose  = () => setEditingStep(null);
  const handleSave   = () => updateStep(editingStep.groupId, step.id, step);

  const setField = (field, value) =>
    setEditingStep(prev => ({ ...prev, step: { ...prev.step, [field]: value } }));

  const setAdditionalField = (name, value) =>
    setEditingStep(prev => ({
      ...prev,
      step: { ...prev.step, additional_fields: { ...prev.step.additional_fields, [name]: value } },
    }));

  return (
    <div
      onClick={handleClose}
      style={{ position: "fixed", inset: 0, zIndex: 1050, background: "rgba(15,23,42,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, animation: "_backdropIn .2s ease" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ fontFamily: ff, width: "100%", maxWidth: 780, background: C.card, borderRadius: C.r16, boxShadow: "0 32px 80px rgba(15,23,42,.35), 0 8px 24px rgba(15,23,42,.08)", overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "92vh", border: `1px solid ${C.border}`, animation: "_modalIn .25s cubic-bezier(.34,1.56,.64,1)" }}
      >

        {/* ── Header ── */}
        <ModalHeader onClose={handleClose} />

        {/* ── Body ── */}
        <div className="_em-scroll" style={{ padding: 24, overflowY: "auto", flex: 1, background: C.body, display: "flex", flexDirection: "column", gap: 18, animation: "_fadeUp .2s ease" }}>

          {/* Task Name */}
          <div style={{ background: C.card, borderRadius: C.r10, border: `1px solid ${C.border}`, padding: "16px 18px" }}>
            <SectionLabel required>Task Name</SectionLabel>
            <StyledInput
              value={step.title}
              onChange={e => setField("title", e.target.value)}
              placeholder="Enter task name…"
            />
          </div>

          {/* Status */}
          <div style={{ background: C.card, borderRadius: C.r10, border: `1px solid ${C.border}`, padding: "16px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <SectionLabel>Status</SectionLabel>
              <StatusBadge status={step.status || statusOptions[0] || "pending"} />
            </div>
            <StyledSelect
              value={step.status}
              onChange={e => setField("status", e.target.value)}
            >
              {statusOptions.map(s => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1).replace("-", " ")}
                </option>
              ))}
            </StyledSelect>
          </div>

          {/* Documentation */}
          <div style={{ background: C.card, borderRadius: C.r10, border: `1px solid ${C.border}`, padding: "16px 18px" }}>
            <SectionLabel hint="Optional">Documentation</SectionLabel>
            {RichTextEditor ? (
              <RichTextEditor
                value={step.documentation}
                onChange={val => setField("documentation", val)}
                placeholder="Add documentation, notes, or instructions…"
              />
            ) : (
              <textarea
                value={step.documentation || ""}
                onChange={e => setField("documentation", e.target.value)}
                placeholder="Add documentation, notes, or instructions…"
                rows={5}
                style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${C.borderMid}`, borderRadius: C.r8, fontSize: 14, fontFamily: ff, color: C.ink, outline: "none", background: C.card, resize: "vertical", lineHeight: 1.7, transition: "border-color .15s" }}
                onFocus={e => { e.target.style.borderColor = C.borderFocus; e.target.style.boxShadow = "0 0 0 2px #4338ca33"; }}
                onBlur={e => { e.target.style.borderColor = C.borderMid; e.target.style.boxShadow = "none"; }}
              />
            )}
          </div>

          {/* Shared in Portal */}
          <div style={{ background: C.card, borderRadius: C.r10, border: `1px solid ${C.border}`, padding: "16px 18px" }}>
            <SectionLabel>Portal Visibility</SectionLabel>
            <PortalToggle
              checked={!!step.sharedInPortal}
              onChange={val => setField("sharedInPortal", val)}
            />
          </div>

          {/* Dependencies */}
          {availableSteps.length > 0 && (
            <div style={{ background: C.card, borderRadius: C.r10, border: `1px solid ${C.border}`, padding: "16px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <SectionLabel hint="Tasks that must complete before this one">Dependencies</SectionLabel>
                {step.dependencies?.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: C.indigo100, color: C.indigo700, border: `1px solid ${C.indigo200}`, fontFamily: ff }}>
                    {step.dependencies.length} selected
                  </span>
                )}
              </div>
              <DependencyList
                availableSteps={availableSteps}
                selectedIds={step.dependencies || []}
                onChange={ids => setField("dependencies", ids)}
              />
            </div>
          )}

          {/* Additional Fields */}
          {additionalFields.length > 0 && (
            <div style={{ background: C.card, borderRadius: C.r10, border: `1px solid ${C.border}`, padding: "16px 18px" }}>
              <SectionLabel>Additional Fields</SectionLabel>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginTop: 4 }}>
                {additionalFields.map(field => (
                  <div key={field.name}>
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: ".05em", textTransform: "uppercase", fontFamily: ff }}>
                        {field.name}
                      </span>
                      <span style={{ fontSize: 11, color: C.faint, fontFamily: ffMono, marginLeft: 6 }}>
                        ({field.type.replace("_", " ")})
                      </span>
                    </div>
                    <FieldEditInput
                      field={field}
                      value={step.additional_fields?.[field.name]}
                      onChange={val => setAdditionalField(field.name, val)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 24px", borderTop: `1px solid ${C.border}`, background: C.card, flexShrink: 0 }}>
          <button
            type="button"
            onClick={handleClose}
            className="_em-btn-ghost"
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 16px", height: 36, borderRadius: C.r8, border: `1.5px solid ${C.borderMid}`, background: C.card, color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: ff, transition: "all .12s", outline: "none" }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            className="_em-btn-primary"
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "0 20px", height: 36, borderRadius: C.r8, background: `linear-gradient(135deg, ${C.indigo700}, ${C.indigo600})`, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: ff, boxShadow: "0 2px 8px rgba(67,56,202,0.40)", transition: "all .15s", outline: "none", letterSpacing: ".01em" }}
            onMouseEnter={e => { e.currentTarget.style.opacity = ".88"; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7l3.5 3.5L12 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Save Changes
          </button>
        </div>

      </div>
    </div>
  );
}